import {
  OpmFirstLastWaterfallDictionaryDto,
  OpmFirstLastWaterfallDto,
  FirstLastSummaryIndexDictionaryDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { l } from '@locale/setupI18n';
import { InstrumentType, TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import { enumKeyByValue } from '@app/shared/helpers';

type TransactionTypeKey =
  | 'infusionTransactions'
  | 'redemptionTransactions'
  | 'cashPayTransactions'
  | 'dividendTransactions';

const transactionTypeMap: Record<TransactionType, TransactionTypeKey> = {
  [TransactionType.Infusion]: 'infusionTransactions',
  [TransactionType.Redemption]: 'redemptionTransactions',
  [TransactionType.CashPaid]: 'cashPayTransactions',
  [TransactionType.Dividend]: 'dividendTransactions',
};

export const getTransactionTypeKey = (transactionType: TransactionType) => {
  return transactionTypeMap[transactionType];
};

type Transaction = { eventNarrative: string | number; amount: number; discountFactor: number };
type TransactionValueKey = 'amount' | 'discountFactor';
export const simulationSectionsDefinition = ['first', 'last'] as (keyof OpmFirstLastWaterfallDto)[];

export const getSimulationKeys = (
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto,
  selectedYear: string
) =>
  simulationSectionsDefinition.map((section) => Object.keys(opmWaterfall[selectedYear][section]));

export const getTransactionValues = (
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto,
  selectedYear: string,
  selectedInstrument: string,
  transactionType: TransactionType,
  transactionValueKey: TransactionValueKey
) => {
  const transactionLookup: {
    [transactionName: string]: {
      [section in keyof OpmFirstLastWaterfallDto]?: { [simKey: string]: number };
    };
  } = {};

  const isOrdinaryEquity = instrumentIsOrdinaryEquity(
    opmWaterfall,
    selectedYear,
    selectedInstrument
  );

  simulationSectionsDefinition.forEach((section) => {
    Object.entries(opmWaterfall[selectedYear][section]).forEach(([simKey, _]) => {
      const summary = (opmWaterfall[selectedYear][section] as FirstLastSummaryIndexDictionaryDto)[
        simKey
      ].instruments[selectedInstrument];
      const transactions = summary[getTransactionTypeKey(transactionType)] || [];
      const multipleTransactions = transactions.length > 1;
      transactions.forEach((tx: Transaction, index: number) => {
        const transactionName = getTransactionName(
          transactionType,
          index + 1,
          multipleTransactions,
          isOrdinaryEquity
        );
        if (!transactionLookup[transactionName]) {
          transactionLookup[transactionName] = {};
        }
        if (!transactionLookup[transactionName][section]) {
          transactionLookup[transactionName][section] = {};
        }
        transactionLookup[transactionName][section]![simKey] = tx[transactionValueKey];
      });
    });
  });

  return transactionLookup;
};

const instrumentIsOrdinaryEquity = (
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto,
  selectedYear: string,
  selectedInstrument: string
) => {
  const firstSection = opmWaterfall[selectedYear]?.first;
  const firstSimKey = firstSection ? Object.keys(firstSection)[0] : undefined;
  const instrument = firstSimKey
    ? firstSection[firstSimKey].instruments[selectedInstrument]
    : undefined;
  return (
    instrument?.instrumentType === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity)
  );
};

const getTransactionName = (
  transactionType: TransactionType,
  index: number,
  multipleTransactions: boolean,
  isOrdinaryEquity: boolean
) => {
  let transactionSuffix = null;
  if (multipleTransactions) {
    transactionSuffix = index.toString();
  }

  switch (transactionType) {
    case TransactionType.Infusion:
      return applySuffix(l('_Infusion'), transactionSuffix);
    case TransactionType.Redemption:
      return applySuffix(isOrdinaryEquity ? l('_Buyback') : l('_Redemption'), transactionSuffix);
    case TransactionType.CashPaid:
      return l('_CashInterest');
    case TransactionType.Dividend:
      return l('_Dividends');
  }
};

const applySuffix = (label: string, labelSuffix?: string | null) =>
  labelSuffix ? `${label} ${labelSuffix}` : label;
